/** Lazy GA4 (gtag.js) loader. Only ever called once the visitor has granted
 *  analytics consent, so no Google request is made before then. Idempotent. */

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

let loaded = false

export function loadGa4(measurementId: string): void {
  if (loaded || typeof window === 'undefined' || typeof document === 'undefined') return
  loaded = true

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
  document.head.appendChild(script)

  const dataLayer = (window.dataLayer = window.dataLayer ?? [])
  // gtag.js only treats a dataLayer entry as a command when it is the
  // `arguments` object — it uses Array.isArray to separate gtag commands from
  // GTM data pushes. A real Array (what rest params produce) is ignored, so
  // `config` never runs and no page_view / `/g/collect` is sent. Match Google's
  // canonical snippet exactly: a regular function that pushes `arguments`.
  // The rest param is only for call-site arity; we must push the `arguments`
  // object itself (array-LIKE but not an Array), never the rest array.
  function gtag(..._args: unknown[]): void {
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments)
  }
  window.gtag = gtag
  gtag('js', new Date())
  // GA4 Enhanced Measurement (on by default) emits the page_view here and on
  // subsequent SPA history changes — no manual per-route tracking needed for v1.
  gtag('config', measurementId)
}

/** Send one GA4 event. Consent-gated BY CONSTRUCTION: `window.gtag` is defined
 *  only by loadGa4() above, and the consent banner calls that only after the
 *  visitor accepts (there is no gtag snippet in index.html). So before consent
 *  this is a silent no-op — nothing is queued, nothing is sent, nothing is kept.
 *  Params must stay coarse and aggregate-safe: never a name, email, user id, or
 *  slug — a club id is allowed on the club UX names and banned on the four
 *  conversions, and `track.ts` holds that argument.
 *
 *  MEASURING SOMETHING MUST NEVER BREAK IT. `gtag` is third-party code we hand a
 *  call to at runtime — Google's own loader, or whatever a tag manager or a
 *  browser extension has put in its place — and since 2026-08-26 every club UX
 *  step passes through here, from inside click handlers and effects that used to
 *  reach no sink at all. A throw from an analytics call would cost a press. */
export function sendGaEvent(
  name: string,
  params?: Record<string, string | number | boolean>,
): void {
  if (typeof window === 'undefined') return
  const gtag = window.gtag
  if (typeof gtag !== 'function') return
  try {
    gtag('event', name, params ?? {})
  } catch {
    /* a broken tag is not the caller's problem */
  }
}
